import React, { useState, useEffect } from "react";
import ContentBox from "../components/ContentBox";
import HabboImage from "../components/HabboImage";
import { useNavigate } from "react-router-dom";
import { getStaffTeams } from "../services/api";

const PLATFORM_CONFIG = {
    twitch:    { base: "https://www.twitch.tv/",     label: "Watch on Twitch!" },
    youtube:   { base: "https://www.youtube.com/@",  label: "Watch on YouTube!" },
    tiktok:    { base: "https://www.tiktok.com/@",   label: "Watch on TikTok!" },
    x:         { base: "https://x.com/",             label: "Follow on X!" },
    instagram: { base: "https://www.instagram.com/", label: "Follow on Instagram!" },
};

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const buildSocialLink = (platform, handle) => {
    if (!platform || !handle) return null;
    const config = PLATFORM_CONFIG[String(platform).toLowerCase()];
    if (!config) return null;
    return { href: `${config.base}${handle}`, label: config.label };
};

const formatJoined = (month, year) => {
    if (!year) return null;
    return month ? `Joined ${MONTHS[month - 1]} ${year}` : `Joined ${year}`;
};

const EXACT_PIXEL_IMAGE = {
    width: "auto",
    height: "auto",
    maxWidth: "none",
    maxHeight: "none",
    objectFit: "none",
    flexShrink: 0,
};

function TeamCard({ member }) {
    const navigate = useNavigate();
    const social = buildSocialLink(member.socialPlatform, member.socialHandle);
    const joined = formatJoined(member.startMonth, member.startYear);

    return (
        <ContentBox
            theme="default"
            tint={member.tint || undefined}
            icon={member.icon || null}
            header={member.username}
            className="h-full"
        >
            <div className="flex gap-3">
                <div className="w-[110px] h-[110px] shrink-0 border border-black bg-gray-200 overflow-hidden flex items-center justify-center">
                    <HabboImage
                        habbo={member.username}
                        hotel="COM"
                        direction={2}
                        head_direction={2}
                        gesture="sml"
                        className="relative top-4"
                        style={{ ...EXACT_PIXEL_IMAGE, scale: 2 }}
                        alt={member.username}
                    />
                </div>

                <div className="min-w-0 flex-1 flex flex-col">
                    <div className="font-['Jersey_10'] tracking-widest uppercase text-[18px] leading-[20px]">
                        {member.role}
                    </div>

                    <div className="flex flex-row items-center gap-2 border-y border-offwhite-100 py-1 mt-1">
                        {joined && <div className="text-black/60 text-xs">{joined}</div>}
                        <button
                            type="button"
                            onClick={() => navigate(`/members/profile/${member.username}`)}
                            className="text-black/40 text-xs ml-auto hover:underline no-underline"
                        >
                            → View my profile...
                        </button>
                    </div>

                    <div className="mt-2 text-[14px] leading-[17px]">
                        {member.blurb && <p>{member.blurb}</p>}
                        {social && (
                            <p className="mt-2">
                                <b>➤ <a
                                    href={social.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:underline text-maroon"
                                >{social.label}</a></b>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </ContentBox>
    );
}

function About() {
    const [teams, setTeams] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        getStaffTeams()
            .then((response) => {
                if (cancelled) return;
                if (response?.success) {
                    setTeams(response.teams || []);
                } else {
                    setHasError(true);
                }
            })
            .catch(() => {
                if (!cancelled) setHasError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    return (
        <div className="w-full p-5">
            <style>{`p { margin-bottom: 10px; }`}</style>

            <div className="grid grid-cols-1 gap-4 lg2:gap-6 lg2:grid-cols-[360px_minmax(0,1fr)] items-start">
                <div className="min-w-0">
                    <ContentBox>
                        <div className="font-['Jersey_10'] tracking-widest text-xl leading-[20px] uppercase">
                            About Frankly Origins
                        </div>
                        <div className="mt-3 text-md">
                            <p>
                                To begin with, let us thank you for visiting! Frankly is a passion project created and run by a dedicated team of Habbo Hotel: Origins players, and your engagement means the world to us as we strive to bring joy, excitement, and quality-of-life tools to the wider community.
                            </p>
                            <p>
                                Frankly itself is the carefully sculpted love child of Steve and Rhyss. Initially thought up as a “that’ll never happen” idea by Steve, one early November afternoon in 2025, Rhyss dropped the line, “I want to make a fansite”, and the code started flying.
                            </p>
                            <p>
                                We knew that Origins desperately lacked a number of resources - things like furni images and easier, free tools for entering competitions, making graphics, and sharing your love for Origins - but it also lacked clarity. Information is often spread across multiple platforms, relying on user interaction to piece together the full picture. We quickly realised that Frankly needed to bring all of this information together, alongside user opinions, to give a clear view of exactly what’s going on within the game.
                            </p>
                            <p>
                                We called on help from the best of the best and put in more sleepless nights than adults with responsibilities probably should, all to bring you the ultimate multi-tool for the Origins universe.
                            </p>
                            <p>
                                Please enjoy your stay, join the community, and remember - as long as you’re having a good time, frankly, it’s all that matters!
                            </p>
                        </div>
                    </ContentBox>
                </div>

                <div className="min-w-0">
                    <div className="md:float-right md:-mb-20">
                        <h1 className="text-right font-['Jersey_10'] tracking-widest uppercase text-[32px] text-white/90">
                            Meet the Team!
                        </h1>
                    </div>

                    {isLoading && (
                        <div className="font-['Jersey_10'] tracking-widest uppercase text-[18px] text-white/70 pt-4">
                            Loading the team...
                        </div>
                    )}

                    {!isLoading && hasError && (
                        <div className="font-['Jersey_10'] tracking-widest uppercase text-[18px] text-white/70 pt-4">
                            The team is having a tea break - please try again shortly.
                        </div>
                    )}

                    {!isLoading && !hasError && teams.map((team) => (
                        <div key={team.teamId} className="mt-6 first:mt-0">
                            <div className="flex items-center gap-3 mb-2">
                                <h2 className="font-['Jersey_10'] tracking-widest uppercase text-[20px] text-offwhite bg-maroon px-3 py-1 border border-black shadow-[2px_2px_0px_rgba(0,0,0,0.2)]">
                                    {team.teamName}
                                </h2>
                                <div className="flex-1 h-px bg-white/25" />
                                <span className="font-['Jersey_10'] tracking-widest uppercase text-[14px] text-white/50 shrink-0">
                                    {team.members.length} member{team.members.length === 1 ? '' : 's'}
                                </span>
                            </div>

                            <div className={`grid gap-4 items-stretch auto-rows-fr ${
                                team.members.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'
                            }`}>
                                {team.members.map((member) => (
                                    <TeamCard key={member.memberId} member={member} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default About;
